import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { searchUsers } from '@/adapters/user';
import { ISearchUserReq, ISearchUserRes, IUser } from '@/interfaces/user';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { isFullAccess } from '@/utils/permissionUtils';
import { AxiosResponse } from 'axios';
import { EUserRole } from '@/enums/EUserRole';
import { twMerge } from 'tailwind-merge';

interface IProps {
  value?: string;
  size?: SizeType;
  isAll?: boolean;
  handleSelectData: (id: string, user?: IUser) => void;
  businessUnitId?: number;
  customClassName?: string;
  allowClear?: boolean;
  /** Filter users by role (e.g. Employee = Commercial Team) */
  role?: EUserRole;
}

const _criteria: ISearchUserReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: -1,
  pageStart: 1,
  search: '',
};

const UserSelector = ({
  value,
  size = 'large',
  handleSelectData,
  isAll = false,
  businessUnitId,
  customClassName,
  allowClear = false,
  role,
}: IProps) => {
  const isAdmin = isFullAccess();
  const [criteria, setCriteria] = useState<ISearchUserReq>({
    ..._criteria,
    role,
  });
  const { data: customers, isLoading } = useQuery<
    AxiosResponse<ISearchUserRes>,
    Error
  >({
    queryKey: ['fetchUser', criteria],
    queryFn: async () => await searchUsers({ ...criteria }),
  });

  const groupOptions =
    customers?.data?.data?.map(g => {
      return {
        value: g.id,
        label: `${g.firstName} ${g.lastName}`,
      };
    }) || [];

  const allOptions = [{ value: '', label: 'ทั้งหมด' }, ...groupOptions];

  useEffect(() => {
    setCriteria(prev => ({
      ...prev,
      businessUnitId: businessUnitId || undefined,
      role,
    }));
  }, [businessUnitId, role]);

  const onchange = (id: string) => {
    const user = customers?.data?.data?.find(c => c.id == id);
    handleSelectData(id, user);
  };

  return (
    <Select
      value={value}
      disabled={!isAdmin}
      loading={isLoading}
      className={twMerge('md:min-w-[150px] w-full', customClassName)}
      size={size}
      placeholder="เลือกผู้ใช้งาน"
      onChange={onchange}
      options={isAll ? allOptions : [...groupOptions]}
      allowClear={allowClear}
    />
  );
};

export default UserSelector;
